import { useMemo } from 'react';
import { useMatches } from 'react-router';

import sidebarData from '../navigation/sidebar-data';

type BreadcrumbItem = {
  title: string;
  path?: string;
  isLast: boolean;
};

export function useBreadcrumbs() {
  const matches = useMatches();

  return useMemo(() => {
    const matchesWithData = matches.filter((match) => match.pathname !== '/');

    if (matchesWithData.length === 0) {
      return [{ title: 'Home', path: '/', isLast: true }];
    }

    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'Home', path: '/', isLast: false },
    ];
    const lastMatch = matchesWithData[matchesWithData.length - 1];
    const targetPath = lastMatch.pathname;
    let found = false;

    const buildBreadcrumbPath = () => {
      const reversedItems: BreadcrumbItem[] = [];

      const findPathInSidebar = (path: string): boolean => {
        for (const section of sidebarData) {
          if (section.url === path) {
            reversedItems.push({
              title: section.title,
              path: section.url,
              isLast: path === targetPath,
            });
            return true;
          }

          if (section.items) {
            for (const item of section.items) {
              if (item.url === path) {
                reversedItems.push({
                  title: item.title,
                  path: item.url,
                  isLast: path === targetPath,
                });

                reversedItems.push({
                  title: section.title,
                  path: section.url,
                  isLast: false,
                });

                return true;
              }
            }
          }
        }
        return false;
      };

      if (findPathInSidebar(targetPath)) {
        for (let i = reversedItems.length - 1; i >= 0; i--) {
          breadcrumbs.push(reversedItems[i]);
        }
        return true;
      }

      return false;
    };

    found = buildBreadcrumbPath();

    // If no match found in sidebar data, use the last segment of the path
    // unlikely case, just in case though.
    if (!found) {
      const pathSegments = targetPath.split('/');
      const title = pathSegments[pathSegments.length - 1]
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      breadcrumbs.push({
        title,
        path: targetPath,
        isLast: true,
      });
    }

    const result = breadcrumbs.map((item, index) => ({
      ...item,
      isLast: index === breadcrumbs.length - 1,
    }));

    return result;
  }, [matches]);
}
